import { Fragment, useMemo } from 'react';
import { CSSTransition } from 'react-transition-group';
import { useEffectOnce } from 'react-use';
import { ButtonHeight } from '~/components/ui/Button';
import { CloseButton } from '~/components/ui/CloseButton';
import { PanelControls } from '~/components/ui/PanelControls';
import { ShopContainer } from '~/components/ui/Shop';
import { ShopBalance } from '~/components/ui/ShopBalance';
import { ShopHead } from '~/components/ui/ShopHead';
import { ShopPreview } from '~/components/ui/ShopPreview';
import { Text } from '~/components/ui/Text';
import { rage, useInputAction, useObservable } from '~/rage';
import { InputAction } from '~/shared';
import logo from '../../assets/images/logo.svg';
import { CaseResult } from '../../components/CaseResult';
import { CasesOverlay } from '../../components/CasesOverlay';
import { CategoryTab } from '../../components/CategoryTab';
import { Handling } from '../../components/Handling';
import { ListTab } from '../../components/ListTab';
import { Modal } from '../../components/Modal';
import { SetupTuning } from '../../components/SetupTuning';
import { useModal, useShopPreview } from '../../hooks';
import {
	resetStore,
	setActiveStage,
	setCaseResultVisible,
	StageTypes,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import {
	StyledBoost,
	StyledControlsContainer,
	StyledRightContainer,
} from './TechTuning.styled';

const TechTuning = () => {
	const dispatch = useLayoutDispatch();
	const activeStage = useLayoutSelector(
		(state) => state.tuningShop.activeStage,
	);
	const modalVisible = useLayoutSelector(
		(state) => state.handling.modalVisible,
	);
	const modalState = useObservable(rage.modal.getCurrentModal());
	const shopPreview = useShopPreview();
	const activeList = useLayoutSelector((state) => state.tuningShop.activeList);
	const activeListIndex = useLayoutSelector(
		(state) => state.tuningShop.activeListIndex,
	);
	const casesOverlayVisible = useLayoutSelector(
		(state) => state.tuningShop.casesOverlayVisible,
	);
	const caseResultVisible = useLayoutSelector(
		(state) => state.tuningShop.caseResultVisible,
	);
	const { handleUnsavedChangesExitModal } = useModal();

	const item = useMemo(() => {
		return activeList[activeListIndex || 0];
	}, [activeList, activeListIndex]);

	const previewBoost = useMemo(() => {
		if (!item || !item?.properties?.boost) return '';
		return `+${item?.properties?.boost}%`;
	}, [item]);

	const rightControls = [
		{
			text: 'Выйти',
			keys: ['Esc'],
			onClick: () => rage.techTuning.exitMenu(),
		},
	];

	useInputAction(InputAction.CommonMenuClose, () => {
		if (caseResultVisible) {
			dispatch(setCaseResultVisible(false));
			return;
		}

		if (casesOverlayVisible || modalVisible || modalState) {
			return;
		}

		if (activeStage === StageTypes.Handling) {
			handleUnsavedChangesExitModal();
			return;
		}

		if (activeStage === StageTypes.List) {
			dispatch(setActiveStage(StageTypes.Category));
			return;
		}

		rage.techTuning
			.exitMenu()
			.catch((err) => rage.notifications.showException(err));
	});

	useEffectOnce(() => {
		rage.techTuning
			.fetchInstalledItems()
			.catch((err) => rage.notifications.showException(err));
		rage.handlingTuning
			.fetchValues()
			.catch((err) => rage.notifications.showException(err));

		return () => {
			dispatch(resetStore());
		};
	});

	return (
		<Fragment>
			<StyledRightContainer>
				<ShopBalance />
				<CloseButton
					height={ButtonHeight.Height40}
					iconSize={16}
					onClick={() => rage.techTuning.exitMenu()}
				/>
			</StyledRightContainer>
			<ShopContainer>
				<ShopHead
					img={logo}
					text={'Станция тюнинга\nи настройки транспорта'}
					height={30}
				/>
				{activeStage === StageTypes.Category && <CategoryTab />}
				{activeStage === StageTypes.List && <ListTab />}
				{activeStage === StageTypes.Handling && <Handling />}
			</ShopContainer>
			<StyledControlsContainer>
				<PanelControls controls={rightControls} align="right" />
			</StyledControlsContainer>
			<SetupTuning />
			<ShopPreview style={{ top: 160, right: 220 }} {...shopPreview}>
				<StyledBoost>
					<Text style={{ fontSize: 16, opacity: 0.5, color: '#ffffff' }}>
						Характеристики
					</Text>
					<Text style={{ fontSize: 16, color: '#00FF37', fontWeight: 700 }}>
						{previewBoost}
					</Text>
				</StyledBoost>
			</ShopPreview>
			<CSSTransition
				in={modalVisible}
				classNames={'modal'}
				timeout={500}
				unmountOnExit
			>
				<Modal />
			</CSSTransition>
			<CSSTransition
				in={casesOverlayVisible}
				classNames={'overlay'}
				timeout={300}
				unmountOnExit
			>
				<CasesOverlay />
			</CSSTransition>
			<CSSTransition
				in={caseResultVisible}
				classNames={'result'}
				timeout={300}
				unmountOnExit
			>
				<CaseResult />
			</CSSTransition>
		</Fragment>
	);
};

export { TechTuning };
